<template>
    <div ref="componentRef" class="min-h-screen bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div v-if="loading" class="text-center py-3">
                <Loader size="large" />
            </div>
            <template v-else>
                <div class="text-center mb-2.5">
                    <h1 class="font-header text-[3rem] xl:text-[3rem] font-bold text-primary mb-1.5">
                        {{ pageData?.title || 'Page' }}
                    </h1>
                    <div v-if="pageData?.description"
                        class="font-body text-[1.125rem] text-gray-700 max-w-4xl mx-auto leading-[1.75] content-page-description"
                        v-html="pageData.description">
                    </div>
                </div>

                <div v-if="pageData?.sections && pageData.sections.length > 0"
                    class="max-w-4xl mx-auto space-y-[0.196rem]">
                    <div v-for="(section, index) in pageData.sections" :key="index"
                        class="p-2.5 md:p-4 prose max-w-none">
                        <h2 v-if="section.title"
                            class="font-header text-[1.5rem] md:text-[1.5rem] font-bold text-primary mb-1">{{
                                section.title
                            }}
                        </h2>
                        <div class="font-body text-[1.125rem] text-gray-700 leading-[1.75] content-page-section"
                            v-html="section.content">
                        </div>
                    </div>
                </div>
                <div v-else-if="!pageData?.description" class="max-w-4xl mx-auto">
                    <div class="p-8 xl:p-12 text-center text-gray-500 font-body text-[1.125rem] leading-[1.75]">
                        <p>Content coming soon...</p>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { publicService } from '../services/public'
import Loader from './Loader.vue'
import { useLoadingState } from '../composables/useLoadingState'
import { useDataTranslation } from '../composables/useDataTranslation'

const props = defineProps({
    slug: {
        type: String,
        required: true
    }
})

const componentRef = ref(null)
const pageData = ref(null)
const { loading, setLoading } = useLoadingState(true)

const { handleLanguageChange } = useDataTranslation(componentRef, loading, '.min-h-screen.bg-white', setLoading)

onMounted(() => {
    window.addEventListener('language-changed', handleLanguageChange)
})

onUnmounted(() => {
    window.removeEventListener('language-changed', handleLanguageChange)
})

const getPageTitle = (slug) => {
    const slugToTitle = {
        'products': 'Products and Services',
        'terms': 'Terms of Use',
        'privacy': 'Privacy Policy',
        'cookies': 'Cookies',
        'guidelines': 'Our Guidelines'
    }
    return slugToTitle[slug] || slug
}

const loadPage = async () => {
    setLoading(true)
    try {
        const pageTitle = getPageTitle(props.slug)
        const data = await publicService.getPage(pageTitle)
        pageData.value = data
    } catch (error) {
        console.error('Failed to load page:', error)
        pageData.value = { title: props.slug, description: null, sections: [] }
    } finally {
        setLoading(false)
    }
}

watch(() => props.slug, () => {
    loadPage()
}, { immediate: false })

onMounted(() => {
    loadPage()
})
</script>


<style scoped>
.prose {
    color: #374151;
}

/* Headers */
.prose :deep(h1),
.prose :deep(h2),
.prose :deep(h3),
.prose :deep(h4) {
    font-family: 'EB Garamond', serif;
    font-weight: bold;
    color: #111111;
    margin-top: 0.05em;
    margin-bottom: 0.025em;
}

:deep(h1) {
    font-size: 2.5rem;
    font-weight: bold;
    color: #111111;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
}

:deep(h2) {
    font-size: 2rem;
    font-weight: bold;
    color: #111111;
    margin-top: 1.25rem;
    margin-bottom: 0.75rem;
}

:deep(h3) {
    font-size: 1.5rem;
    font-weight: bold;
    color: #111111;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
}

/* Font Sizes */
:deep(.ql-size-small) {
    font-size: 0.75rem;
}

:deep(.ql-size-large) {
    font-size: 1.5rem;
}

:deep(.ql-size-huge) {
    font-size: 2.5rem;
}

/* Blockquotes */
:deep(blockquote) {
    border-left: 4px solid #ccc;
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
    color: #666;
}

/* Code Blocks */
:deep(pre) {
    background-color: #f4f4f4;
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
    margin: 1rem 0;
}

:deep(code) {
    background-color: #f4f4f4;
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
}

:deep(pre code) {
    background-color: transparent;
    padding: 0;
}

/* Images */
:deep(img) {
    max-width: 100%;
    height: auto;
    margin: 1rem 0;
}

/* Videos and Iframes */
:deep(iframe),
:deep(video) {
    max-width: 100%;
    height: auto;
    margin: 1rem 0;
}

/* Text Alignment */
:deep(.ql-align-center) {
    text-align: center;
}

:deep(.ql-align-right) {
    text-align: right;
}

:deep(.ql-align-justify) {
    text-align: justify;
}

:deep([style*='text-align: center']) {
    text-align: center;
}

:deep([style*='text-align: right']) {
    text-align: right;
}

:deep([style*='text-align: justify']) {
    text-align: justify;
}

/* Font Families */
:deep(.ql-font-serif) {
    font-family: Georgia, 'Times New Roman', serif;
}

:deep(.ql-font-monospace) {
    font-family: 'Courier New', Courier, monospace;
}

/* Paragraphs */
.prose :deep(p) {
    font-family: 'Georgia', serif;
    line-height: 1.75;
    margin-bottom: 0.075em;
}

:deep(p) {
    margin-bottom: 1rem;
}

/* Lists */
.prose :deep(ul) {
    list-style-type: disc;
    margin-left: 1.5em;
    margin-bottom: 0.1em;
}

.prose :deep(ol) {
    list-style-type: decimal;
    margin-left: 1.5em;
    margin-bottom: 0.1em;
}

.prose :deep(li) {
    margin-bottom: 0.05em;
}

:deep(ul),
:deep(ol) {
    margin: 1rem 0;
    padding-left: 1.5rem;
}

:deep(li) {
    margin-bottom: 0.5rem;
}

/* Links limited to content sections */
.content-page-description :deep(a),
.content-page-section :deep(a) {
    color: #2563eb;
    text-decoration: underline;
    transition: color 0.2s;
    font-family: inherit;
}

.content-page-description :deep(a):hover,
.content-page-section :deep(a):hover {
    color: #1e40af;
}
</style>
